import { useTheme } from '@emotion/react';
import { Formik } from 'formik';
import { useAtomValue } from 'jotai';
import { useResetAtom } from 'jotai/utils';
import React, { useCallback, useEffect, useMemo } from 'react';

import {
  Alert,
  Box,
  Button,
  Divider,
  Drawer,
  EntryList,
  Flex,
  Grid,
  Header,
  Hint,
  Loader,
  Radio,
  RadioGroup,
  Text,
  Toggle,
} from '~/components/blocks';
import { ExternalLink } from '~/components/blocks/ExternalLink';
import { YakumaruGroupCodeUnsetAlert } from '~/components/partials/YakumaruGroupCodeUnsetAlert';
import { YakumaruSettingsErrorMessage } from '~/components/partials/YakumaruSettingsErrorMessage';
import { useGetYakumaruCompanySetting } from '~/hooks/use-get-yakumaru_company_setting';
import { usePractitionerRole } from '~/hooks/use-practitioner-role';
import { settingsDrawerState } from '~/state/settings_systemsettings/atoms';

import { PermissionError } from '../PermissionError';
import { Fields } from './types';
import { useFetchYakumaruOrganizationSetting } from './use-fetch_yakumaru_organization_setting';
import { useUpdateYakumaruOrganizationSetting } from './use-update_yakumaru_organization_setting';
import { validationSchema } from './validation';

const ORGANIZATION_SETTINGS_PATH = '/settings/organization';

export const PrescriptionIdentifyDrawer = () => {
  const theme = useTheme();
  const { verifyRole } = usePractitionerRole();
  const enabledEdit = verifyRole(['admin', 'company_admin']);
  const state = useAtomValue(settingsDrawerState);
  const resetState = useResetAtom(settingsDrawerState);
  const isOpen = state.drawer === 'prescriptionIdentify';

  const { updating, error, update } = useUpdateYakumaruOrganizationSetting();
  const {
    getYakumaruOrganizationSetting,
    loading,
    organizationId,
    pharmacyCode,
    yakumaruOrganizationSetting,
  } = useFetchYakumaruOrganizationSetting();
  const {
    yakumaruCompanySetting,
    getYakumaruCompanySetting,
    called: companySettingCalled,
    error: companySettingError,
    loading: companySettingLoading,
  } = useGetYakumaruCompanySetting();
  const isYakumaruCompanySettingInactive = !yakumaruCompanySetting?.active;
  // 法人IDがないと店舗IDでは連携できないため、薬局コードでの連携に固定する
  // 取得前・取得失敗も法人ID未設定に見えるので、固定するのは取得できたときだけにする(不変条件はAPI側で担保している)
  const isGroupCodeUnset =
    companySettingCalled && !companySettingError && !yakumaruCompanySetting?.groupCode;

  const initialValues = useMemo<Fields>(
    () => ({
      active: yakumaruOrganizationSetting?.active ?? false,
      useCustomSetting:
        isGroupCodeUnset || (yakumaruOrganizationSetting?.useCustomSetting ?? false),
    }),
    [
      isGroupCodeUnset,
      yakumaruOrganizationSetting?.active,
      yakumaruOrganizationSetting?.useCustomSetting,
    ],
  );

  const handleClose = useCallback(() => {
    resetState();
  }, [resetState]);

  const handleSubmit = useCallback(
    async (values: Fields) => {
      if (!organizationId) return;
      if (!values.useCustomSetting && !yakumaruOrganizationSetting?.storeCode) return;
      if (values.useCustomSetting && !pharmacyCode) return;

      try {
        await update({
          variables: {
            input: {
              yakumaruOrganizationSettingId: yakumaruOrganizationSetting?.id ?? null,
              organizationId,
              active: values.active,
              storeCode: yakumaruOrganizationSetting?.storeCode,
              useCustomSetting: values.useCustomSetting,
            },
          },
        });
        handleClose();
      } catch {
        // 何もしない
      }
    },
    [
      organizationId,
      pharmacyCode,
      handleClose,
      update,
      yakumaruOrganizationSetting?.id,
      yakumaruOrganizationSetting?.storeCode,
    ],
  );

  useEffect(() => {
    if (isOpen) {
      getYakumaruCompanySetting();
      getYakumaruOrganizationSetting();
    }
  }, [getYakumaruCompanySetting, getYakumaruOrganizationSetting, isOpen]);

  return (
    <Drawer open={isOpen} onClose={handleClose}>
      <Grid height="100%" gridTemplateRows="min-content 1fr min-content">
        <Header attached="drawer">処方箋読取設定</Header>
        <Formik
          enableReinitialize
          validationSchema={validationSchema}
          initialValues={initialValues}
          onSubmit={handleSubmit}
        >
          {(formik) =>
            loading || companySettingLoading ? (
              <Loader open inside />
            ) : (
              <>
                <Box paddingX={theme.space.xl}>
                  {error && (
                    <Alert status="error" mb={theme.space.m}>
                      {error.message ? (
                        <YakumaruSettingsErrorMessage
                          message={error.message}
                          settingsPath={ORGANIZATION_SETTINGS_PATH}
                        />
                      ) : (
                        <Text size="s">エラーが発生しました</Text>
                      )}
                    </Alert>
                  )}
                  <EntryList>
                    <EntryList.Head>ステータス</EntryList.Head>
                    <EntryList.Body>
                      <RadioGroup>
                        <Radio
                          label="有効"
                          checked={formik.values.active}
                          disabled={updating || !enabledEdit}
                          onChange={() => formik.setFieldValue('active', true)}
                        />
                        <Radio
                          label="無効"
                          checked={!formik.values.active}
                          disabled={updating || !enabledEdit}
                          onChange={() => formik.setFieldValue('active', false)}
                        />
                      </RadioGroup>
                    </EntryList.Body>
                  </EntryList>
                  <Divider />
                  <Flex alignItems="center" justifyContent="space-between">
                    <Flex alignItems="center">
                      <Text size="m" fontWeight="bold">
                        個別設定
                      </Text>
                      <Hint placement="top" marginLeft={theme.space.s}>
                        一部のレセコンをご利用の薬局向けの設定です。
                        <br />
                        案内があった場合のみ選択してください
                      </Hint>
                    </Flex>
                    <Toggle
                      name="useCustomSetting"
                      checked={formik.values.useCustomSetting}
                      disabled={
                        updating ||
                        !enabledEdit ||
                        isYakumaruCompanySettingInactive ||
                        isGroupCodeUnset
                      }
                      onChange={(e) => formik.setFieldValue('useCustomSetting', e.target.checked)}
                    />
                  </Flex>
                  <Divider />
                  {!isYakumaruCompanySettingInactive && isGroupCodeUnset && (
                    <YakumaruGroupCodeUnsetAlert />
                  )}
                  {formik.values.useCustomSetting && !pharmacyCode && (
                    <Alert status="error">
                      <Text size="s">
                        個別設定には薬局コードが必要です。
                        <br />
                        <ExternalLink url="/settings/organization">こちら</ExternalLink>
                        から登録してください
                      </Text>
                    </Alert>
                  )}
                  {formik.values.useCustomSetting && pharmacyCode && (
                    <>
                      <EntryList>
                        <EntryList.Head>都道府県コード</EntryList.Head>
                        <EntryList.Body>{pharmacyCode.slice(0, 2)}</EntryList.Body>
                      </EntryList>
                      <EntryList>
                        <EntryList.Head>医療機関コード</EntryList.Head>
                        <EntryList.Body>{pharmacyCode.slice(-7)}</EntryList.Body>
                      </EntryList>
                    </>
                  )}
                  {isYakumaruCompanySettingInactive && (
                    <Alert status="warning" marginTop={theme.space.m}>
                      <Text size="s">
                        本部での設定が無効のため、この機能を使用することができません
                      </Text>
                    </Alert>
                  )}
                  {!isYakumaruCompanySettingInactive &&
                    !formik.values.useCustomSetting &&
                    !yakumaruOrganizationSetting?.storeCode && (
                      <Alert status="warning" marginTop={theme.space.m}>
                        <Text size="s">
                          本部での設定で店舗IDが未入力のため、この機能を使用することができません
                        </Text>
                      </Alert>
                    )}
                </Box>
                <Drawer.Footer>
                  {!enabledEdit && <PermissionError />}
                  <Button
                    type="button"
                    wide="fill"
                    use="base"
                    size="l"
                    loading={updating}
                    disabled={
                      !enabledEdit ||
                      !formik.isValid ||
                      updating ||
                      isYakumaruCompanySettingInactive ||
                      (formik.values.useCustomSetting
                        ? !pharmacyCode
                        : !yakumaruOrganizationSetting?.storeCode)
                    }
                    onClick={formik.submitForm}
                  >
                    保存
                  </Button>
                </Drawer.Footer>
              </>
            )
          }
        </Formik>
      </Grid>
    </Drawer>
  );
};
